import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, KeyRound } from "lucide-react";
import logo from "../assets/twu-logo.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-sm w-full animate-fade-up" data-testid="reset-password-page">
        <img src={logo} alt="TWU Local 555" className="w-24 h-auto mb-10" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Account Recovery</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">Choose a new password</h1>
        {!token && <p data-testid="reset-missing-token" className="text-sm text-destructive">This reset link is missing its token. Request a new one from the sign-in page.</p>}
        {done ? (
          <div data-testid="reset-success" className="text-sm text-green-700 border border-green-300 bg-green-50 rounded-sm px-3 py-3 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />Password updated. You can now sign in.
          </div>
        ) : token && (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="np" className="text-xs uppercase tracking-[0.15em]">New Password</Label>
              <Input id="np" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input" className="rounded-sm h-11" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp" className="text-xs uppercase tracking-[0.15em]">Confirm Password</Label>
              <Input id="cp" type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input" className="rounded-sm h-11" required />
            </div>
            <p className="text-xs text-muted-foreground">At least 8 characters.</p>
            {error && <div data-testid="reset-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full h-11 rounded-sm bg-black hover:bg-neutral-800 text-white">
              <KeyRound className="w-4 h-4 mr-2" />{loading ? "Please wait…" : "Update password"}
            </Button>
          </form>
        )}
        <Link to="/login" data-testid="reset-back-to-login" className="mt-6 inline-block text-sm text-muted-foreground hover:text-foreground transition-colors">← Back to sign in</Link>
      </div>
    </div>
  );
}
